import React, {useEffect, useState, useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

// COMPONENTS
import Rooms from './Rooms/Rooms';
import MultiRoom from './MultiRoom/MultiRoom';
import AllInclusiveRatePlan from './AllInclusiveRatePlan/AllInclusiveRatePlan';
import RoomFlavorLoader from '../../../../../stories/components/hotels/Loadingpage/RoomTypeLoader/RoomTypeLoader';
import ElitePackageDetailsCard from './ElitePackageContainer/ElitePackageCard';

// TYPES
import {
  GroupFilter,
  RatePlanFilter,
  RfElementType,
} from '@stencil/hotels-cg-reducer/src/details/types';

//ACTIONS
import {updateAppliedFilters} from '@stencil/hotels-cg-reducer/src/details/actions';

import {
  updateDetailsCartRequestData,
  updateDetailsCartUIData,
  setActivePayModeInCart,
} from '@stencil/hotels-cg-reducer/src/details/actions';

// SELECTORS
import {
  appliedFiltersSelector,
  roomsDataSelector,
  multiRoomFlowStatusSelector,
  searchRoomApiRequestSelector,
  roomFiltersDropdownSelector,
  isEntirePropertySelector,
  hotelDetailsSelector,
  isAltAccoSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

// UTILS
import getCgFilteredRooms from '@stencil/hotels-utils/src/details-get-filtered-cg-rooms';
import {detailsNavigationIds} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {flatternArray} from '@stencil/hotels-utils/src/common-utils';

// Omniture
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';

//STYLES
import {SelectHeaderWrapper, WrapperDiv, WrapperSection} from '../../DetailsPage.styles';
import {LargeSectionHeader} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';

const RoomSelection = ({showHeading = false, handlOpenCalendar = () => {}}) => {
  const dispatch = useDispatch();
  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const roomsDataFromCg = useSelector(roomsDataSelector, shallowEqual);
  const appliedFiltersInUrl = useSelector(appliedFiltersSelector);
  const roomsFilterData = useSelector(roomFiltersDropdownSelector);
  const searchRoomApiRequestState = useSelector(searchRoomApiRequestSelector);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const hotelDetails = useSelector(hotelDetailsSelector);

  const isMultiRoom = multiRoomFlowStatus?.isMultiRoomFlow;

  // const getActiveCartPayMode = useSelector(activeCartPayModeSelector);
  // const maldviesPokusInfo = useSelector(maldivesPokusInfoSelector);
  const appliedFilters = useMemo<RatePlanFilter[]>(() => {
    return flatternArray(
      roomsFilterData?.map(roomFilter =>
        roomFilter.ratePlanFilterList?.filter(ratePlanFilter => ratePlanFilter.selected),
      ) || [],
    );
  }, [roomsFilterData]);

  const [currentFilters, setFilters] = useState<RatePlanFilter[]>(
    appliedFilters?.length > 0 ? appliedFilters : ([] as RatePlanFilter[]),
  );

  const onElitePackageFilterSelect = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.RoomSelectionPageUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: detailsClick.roomSelection.elitePackageFilterSelect,
          type: omnitureConfig.ctaType.Card,
          componentName: DetailsPageComponents.ElitePackages,
          destination: '',
        },
      }),
    );
  };

  useEffect(() => {
    if (appliedFilters?.length > 0) {
      setFilters(appliedFilters);
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (searchRoomApiRequestState?.loading) {
      setFilters([]);
    }
  }, [searchRoomApiRequestState?.loading]);

  useEffect(() => {
    dispatch(updateAppliedFilters(currentFilters));
    if (currentFilters && currentFilters.length > 0) {
      let isClearCart = false,
        isPayFilterSelected = false,
        isElitePackageFilterExists = false;
      currentFilters.forEach(item => {
        if (item?.code === 'PACKAGE_RATE') {
          isElitePackageFilterExists = true;
        }
      });
      if (isClearCart && isPayFilterSelected) {
        dispatch(updateDetailsCartRequestData({}));
        dispatch(updateDetailsCartUIData({}));
        dispatch(setActivePayModeInCart(-1));
      }
      if (isElitePackageFilterExists) {
        onElitePackageFilterSelect();
      }
    }
  }, [currentFilters]);

  const filteredRooms = getCgFilteredRooms({currentFilters, roomsDataFromCg});
  const rooms = filteredRooms;
  const filterHeight = 0;

  if (searchRoomApiRequestState?.loading) {
    return <RoomFlavorLoader />;
  }

  return (
    <WrapperSection id={isAltAcco ? detailsNavigationIds.stays : detailsNavigationIds.rooms}>
      <WrapperDiv>
        {showHeading && (
          <SelectHeaderWrapper>
            <LargeSectionHeader>{hotelDetails?.name} Room Types</LargeSectionHeader>
          </SelectHeaderWrapper>
        )}
        <AllInclusiveRatePlan setFilters={setFilters} />
        {isMultiRoom && (
          <MultiRoom
            handlOpenCalendar={handlOpenCalendar}
            hasRecommendedCombos={multiRoomFlowStatus?.hasRecommendedCombos}
            hasOccupancyCombos={multiRoomFlowStatus?.hasOccupancyRooms}
          />
        )}
        <ElitePackageDetailsCard handlOpenCalendar={handlOpenCalendar} />
        <Rooms
          rooms={rooms}
          filterHeight={filterHeight}
          currentFilters={currentFilters}
          setFilters={setFilters}
          handlOpenCalendar={handlOpenCalendar}
        />
      </WrapperDiv>
    </WrapperSection>
  );
};

export default RoomSelection;
